import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChartData, ChartDataset, Chart as ChartJS, ChartOptions } from 'chart.js';
import { Chart } from 'react-chartjs-2';
import { SpotInterface } from 'types';
import { useRevenueContext } from 'contexts/RevenueContext';
import { HTTPError } from 'clients/HTTPClient';
import { currencyWithoutCents } from 'utils/currency';
import Loader from 'components/Loader/Loader';
import {
    ChartId,
    CURRENT,
    GAP,
    getAllTimeStartDate,
    getDataForDailyOrMonthlyGraph,
    getImgPattern,
    GUARANTEE_COLOR,
    GUARANTEE_LINE_COLOR,
    OnOffOption,
    PRIMARY_COLOR,
    RevenueType,
    sharedGraphOptions,
    Timeframe,
} from '../helpers';
import pattern from './BarPattern.png';

type MoMRevenueProps = {
    spot: SpotInterface;
    filters: { revenueType: RevenueType; momYearCompare: OnOffOption; momGuaranteeCompare: OnOffOption };
    setErrorMessage: (error: string) => void;
    isYearToggleOn: boolean;
    setShowYearCompareToggle: (value: boolean) => void;
    isGuaranteeToggleOn: boolean;
    setShowGuaranteeToggle: (value: boolean) => void;
    isAllTime: boolean;
};

type GraphData = {
    [RevenueType.GROSS_REVENUE]: number[];
    [RevenueType.NET_REVENUE]: number[];
    minimumGuarantee: number[];
    guaranteeGap: number[];
    labels: string[];
    suggestedMax: number;
    suggestedMaxWithGuarantee: number;
};

const initialGraphData: GraphData = {
    [RevenueType.GROSS_REVENUE]: [],
    [RevenueType.NET_REVENUE]: [],
    minimumGuarantee: [],
    guaranteeGap: [],
    labels: [],
    suggestedMax: 0,
    suggestedMaxWithGuarantee: 0,
};

function MoMRevenue({
    spot,
    filters,
    setErrorMessage,
    isYearToggleOn,
    setShowYearCompareToggle,
    isGuaranteeToggleOn,
    setShowGuaranteeToggle,
    isAllTime,
}: MoMRevenueProps): JSX.Element {
    const { getMomReport, getMomPreviousYearReport, momCurrentYearReport } = useRevenueContext();
    const [loading, setLoading] = useState(false);
    const [graphData, setGraphData] = useState<GraphData>(initialGraphData);
    const [previousYearGraphData, setPreviousYearGraphData] = useState<GraphData>(initialGraphData);
    const [backgroundPattern, setBackgroundPattern] = useState<ChartDataset<'bar'>['backgroundColor']>(PRIMARY_COLOR);

    // Guarantee overlay is only for Net Revenue, and never alongside the year-comparison view.
    const showGuarantee = isGuaranteeToggleOn && !isYearToggleOn && filters.revenueType === RevenueType.NET_REVENUE;

    const barsBackgroundColor = useMemo(() => {
        return graphData[filters.revenueType].length
            ? [...new Array(graphData[filters.revenueType].length - 1).fill(PRIMARY_COLOR), backgroundPattern]
            : PRIMARY_COLOR;
    }, [graphData, filters, backgroundPattern]);

    const chartRef = useRef<ChartJS<'bar'>>(null);

    const options: ChartOptions<'bar' | 'line'> = useMemo(() => {
        const shared = sharedGraphOptions(showGuarantee ? graphData.suggestedMaxWithGuarantee : graphData.suggestedMax);
        if (isYearToggleOn) {
            if (shared.plugins?.tooltip?.callbacks) {
                shared.plugins.tooltip.callbacks.label = ({ dataIndex, datasetIndex, raw, formattedValue }) => {
                    const isCurrentYear = datasetIndex === 1;
                    // label example: "Dec 2025"
                    const label = isCurrentYear ? graphData.labels[dataIndex] : previousYearGraphData.labels[dataIndex];
                    const currency = typeof raw === 'number' ? currencyWithoutCents.format(raw) : formattedValue;
                    return `${label?.split(' ')[1] || ''}: ${currency}`;
                };
            }
            if (shared.plugins?.datalabels) shared.plugins.datalabels.display = false;
        }
        if (showGuarantee) {
            shared.scales = {
                ...shared.scales,
                x: { ...shared.scales?.x, stacked: true },
                // min: 0 keeps stacked segment proportions honest
                y: { ...shared.scales?.y, stacked: true, min: 0 },
            };
            if (shared.plugins?.tooltip) {
                // Only surface net revenue on hover;
                shared.plugins.tooltip.filter = (tooltipItem) =>
                    tooltipItem.dataset.label === CURRENT ||
                    (tooltipItem.dataset.label === GAP && Number(tooltipItem.raw || 0) > 0);
            }
        }
        return shared;
    }, [graphData.suggestedMax, graphData, previousYearGraphData, isYearToggleOn, showGuarantee]);

    const previousYearDataSet: ChartDataset<'bar', number[]> = useMemo(() => {
        return {
            data: previousYearGraphData[filters.revenueType],
            backgroundColor: '#50ACC6',
            label: 'previous',
            order: 2,
        };
    }, [previousYearGraphData, filters.revenueType]);

    const currentYearDataset: ChartDataset<'bar', number[]> = useMemo(() => {
        return {
            data: graphData[filters.revenueType],
            backgroundColor: barsBackgroundColor,
            label: CURRENT,
            ...(showGuarantee ? { stack: 'revenue', order: 2 } : {}),
        };
    }, [graphData, filters.revenueType, barsBackgroundColor, showGuarantee]);

    const guaranteeGapDataset: ChartDataset<'bar', number[]> = useMemo(() => {
        return {
            data: graphData.guaranteeGap,
            backgroundColor: GUARANTEE_COLOR,
            label: GAP,
            stack: 'revenue',
            datalabels: { display: false },
            order: 3,
        };
    }, [graphData.guaranteeGap]);

    const minimumGuaranteeLineDataset: ChartDataset<'line', number[]> = useMemo(() => {
        return {
            type: 'line' as const,
            data: graphData.minimumGuarantee,
            borderColor: GUARANTEE_LINE_COLOR,
            borderDash: [6, 4],
            borderWidth: 2,
            pointRadius: 0,
            fill: false,
            label: 'Minimum Guarantee',
            datalabels: {
                // Only label the line's first point, so it reads as "Min" once rather than on every month.
                display: (context: { dataIndex: number }) => context.dataIndex === 0,
                formatter: () => 'Min',
                align: 'left',
                anchor: 'start',
                color: '#ffffff',
                backgroundColor: GUARANTEE_LINE_COLOR,
                borderRadius: 12,
                font: { size: 12 },
                padding: {
                    top: 4,
                    bottom: 4,
                    left: 6,
                    right: 6,
                },
            },
            tension: 0,
            order: 1,
        };
    }, [graphData.minimumGuarantee]);

    const data: ChartData<'bar' | 'line', number[], string> = useMemo(() => {
        let datasets: ChartDataset<'bar' | 'line', number[]>[] = isYearToggleOn
            ? [previousYearDataSet, currentYearDataset]
            : [currentYearDataset];
        if (showGuarantee) datasets = [guaranteeGapDataset, ...datasets, minimumGuaranteeLineDataset];
        return {
            labels: isYearToggleOn ? graphData.labels.map((l) => l?.split(' ')[0] || '') : graphData.labels,
            datasets,
        };
    }, [
        previousYearDataSet,
        currentYearDataset,
        guaranteeGapDataset,
        minimumGuaranteeLineDataset,
        isYearToggleOn,
        showGuarantee,
        graphData.labels,
    ]);

    const getRevenueData = useCallback(
        async (isPreviousYear = false) => {
            setLoading(true);
            try {
                if (isPreviousYear) {
                    const response = await getMomPreviousYearReport(spot.uuid);
                    const graphData = getDataForDailyOrMonthlyGraph(response, Timeframe.MONTHLY, true);
                    setPreviousYearGraphData(graphData);
                } else {
                    const response = await getMomReport(spot.uuid, isAllTime ? getAllTimeStartDate(spot) : undefined);
                    const graphData = getDataForDailyOrMonthlyGraph(response, Timeframe.MONTHLY, false);
                    // Year-over-year comparison only applies to the 12-month Monthly view, and only
                    // if the parking lot has been at least 13 months w AirGarage. Never for All Time.
                    if (!isAllTime && graphData.labels.length >= 13) setShowYearCompareToggle(true);
                    else setShowYearCompareToggle(false);
                    // Guarded like the year-compare toggle above: All Time has no single guarantee
                    // deal to compare against, so never leave the toggle rendered (and possibly on)
                    // when the user switches into that view.
                    setShowGuaranteeToggle(!isAllTime && graphData.minimumGuarantee.some((amount) => amount > 0));
                    setGraphData(graphData);
                }
            } catch (e) {
                if (HTTPError.isHTTPError(e)) {
                    setErrorMessage(e.message);
                } else {
                    setErrorMessage(e?.toString() || 'Something went wrong');
                }
            } finally {
                // This is needed to prevent a bug in the graph when updating the data.
                setTimeout(() => setLoading(false), 0);
            }
        },
        [spot.uuid, isAllTime]
    );

    useEffect(() => {
        getRevenueData();
    }, [spot.uuid, isAllTime]);

    useEffect(() => {
        if (momCurrentYearReport && !loading && backgroundPattern === PRIMARY_COLOR) {
            const img = new Image();
            img.src = pattern;
            img.onload = () => {
                const fillPattern = getImgPattern(chartRef.current, img);
                if (fillPattern) setBackgroundPattern(fillPattern);
            };
        }
    }, [momCurrentYearReport, loading]);

    // Refetch on spot change too, or the previous-year bars keep showing the first spot opened.
    useEffect(() => {
        if (isYearToggleOn) getRevenueData(true);
    }, [isYearToggleOn, spot.uuid]);

    return loading || !momCurrentYearReport ? (
        <Loader centered style={{ marginTop: '20px' }} />
    ) : (
        <div id={ChartId.REVENUE_CHART} style={{ paddingBlock: '32px' }}>
            <Chart ref={chartRef} type="bar" data={data} options={options} style={{ maxHeight: '240px' }} />
        </div>
    );
}

export default MoMRevenue;
